import { useState, useEffect, FormEvent } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { Container, Stack } from "@mui/material";

import { useAuth } from "components/auth/AuthProvider";
import SectionHeader from "components/utils/SectionHeader";
import ProtectedRoute from "components/auth/ProtectedRoute";
import { useUserServiceUpdateProfile } from "hooks/user/base";

export const Profile = () => {
  const { user } = useAuth();

  const [allowEditing, setAllowEditing] = useState(false);

  const [email, setEmail] = useState(user.email);
  const [lastName, setLastName] = useState(user.lastName);
  const [firstName, setFirstName] = useState(user.firstName);

  const profile = useUserServiceUpdateProfile();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const body = {
      lastName,
      firstName,
    };

    // attempt user update
    profile.mutate(body);
  };

  const cancelEditing = () => {
    setAllowEditing(!allowEditing);

    setEmail(user.email);
    setLastName(user.lastName);
    setFirstName(user.firstName);
  };

  useEffect(() => {
    if (profile.isSuccess) {
      setAllowEditing(false);
    }
  }, [profile.isSuccess]);

  useEffect(() => {
    setEmail(user.email);
    setLastName(user.lastName);
    setFirstName(user.firstName);
  }, [user]);

  useEffect(() => {
    document.title = "GRACE | profile";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      <Container component="main" maxWidth="sm">
        <SectionHeader title="My profile" icon={<AssignmentIcon />} />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{ mt: 1, color: "text.primary" }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="firstName"
                  name="firstName"
                  label="First name"
                  autoComplete="given-name"
                  value={firstName}
                  disabled={!allowEditing}
                  onChange={(e: any) => setFirstName(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="lastName"
                  name="lastName"
                  label="Last name"
                  autoComplete="family-name"
                  value={lastName}
                  disabled={!allowEditing}
                  onChange={(e: any) => setLastName(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="upn"
                  name="upn"
                  label="UPN"
                  value={user.upn}
                  autoComplete="upn"
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  autoComplete="email"
                  label="Email address"
                  disabled
                  value={email}
                  onChange={(e: any) => setEmail(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="role"
                  name="role"
                  label="Role"
                  type="role"
                  autoComplete="new-role"
                  disabled
                  value={user.role}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Button
                variant={allowEditing ? "outlined" : "contained"}
                onClick={cancelEditing}
                sx={{ mt: 3, mb: 2, mr: 2 }}
              >
                {allowEditing ? "Cancel" : "Edit"}
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={!allowEditing}
                sx={{ mt: 3, mb: 2 }}
              >
                Save
              </Button>
            </Stack>
          </Box>
        </Box>
      </Container>
    </ProtectedRoute>
  );
};
